import React from 'react';

import type { StickyType } from '@core/StickyScroll';

import type {
  DialogAlignment,
  DialogColor,
  DialogVariant,
  DialogWidth,
  SlotOrientation,
} from './styles';

export type DialogContextType = {
  alignment: DialogAlignment;
  color: DialogColor;
  invert: boolean;
  variant: DialogVariant;
  width: DialogWidth;
  headingGroupId?: string;
  sticky: StickyType;
  slotOrientation?: SlotOrientation;
  closeAbsoluteOnMobile: boolean;
};

const defaultContext: DialogContextType = {
  alignment: 'start',
  color: 'white',
  invert: false,
  variant: 'standard',
  width: 'medium',
  sticky: 'none',
  slotOrientation: undefined,
  closeAbsoluteOnMobile: false,
};

const DialogContext = React.createContext<DialogContextType>(defaultContext);

export const useDialogContext = (): DialogContextType => {
  return React.useContext(DialogContext);
};

type Props = { children?: React.ReactNode } & Omit<DialogContextType, 'invert'>;

export const DialogContextProvider = ({
  alignment,
  color,
  headingGroupId,
  variant,
  width,
  children,
  sticky,
  slotOrientation,
  closeAbsoluteOnMobile,
}: Props) => {
  const context = React.useMemo(
    () => ({
      alignment,
      color,
      headingGroupId,
      variant,
      width,
      invert: color !== 'white',
      sticky,
      slotOrientation,
      closeAbsoluteOnMobile,
    }),
    [
      alignment,
      color,
      headingGroupId,
      variant,
      width,
      sticky,
      slotOrientation,
      closeAbsoluteOnMobile,
    ]
  );

  return (
    <DialogContext.Provider value={context}>{children}</DialogContext.Provider>
  );
};
